import { useState, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Session } from '../api/types.js';
import { useAuth } from '../auth/index.js';
import { useDeleteSession, useRenameSession } from '../hooks/use-sessions.js';
import { useRestoreSession } from '../hooks/use-session-actions.js';
import { Button } from './button.js';
import { useNotifications } from '../hooks/use-notifications.js';
import { SessionStatusBadge } from './session-status-badge.js';
import { ConfirmDialog } from './confirm-dialog.js';
import { lastActive } from '../utils/session-order.js';

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function truncateId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}...` : id;
}

const MODEL_LABELS: Record<string, string> = {
  opus: 'Opus',
  sonnet: 'Sonnet',
  haiku: 'Haiku',
};

export const SessionCard = memo(function SessionCard({ session, unread = false, awaitingDecision = false }: { session: Session; unread?: boolean; awaitingDecision?: boolean }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const deleteSession = useDeleteSession(session.serverId);
  const restoreSession = useRestoreSession(session.serverId, session.id);
  const renameSession = useRenameSession(session.serverId, session.id);
  const { hasPermissionPending } = useNotifications();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = user?.role === 'admin';
  const isTerminated = session.status === 'terminated';
  const permissionPending = hasPermissionPending(session.serverId, session.id);

  function handleClick() {
    if (isRenaming) return;
    navigate(`/servers/${session.serverId}/sessions/${session.id}`);
  }

  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation();
    setShowDeleteDialog(true);
  }

  function handleDeleteConfirm() {
    setShowDeleteDialog(false);
    deleteSession.mutate(session.id);
  }

  function handleRestoreClick(e: React.MouseEvent) {
    e.stopPropagation();
    restoreSession.mutate(undefined, {
      onSuccess: () => {
        navigate(`/servers/${session.serverId}/sessions/${session.id}`);
      },
    });
  }

  function handleRenameClick(e: React.MouseEvent) {
    e.stopPropagation();
    setRenameValue(session.name || '');
    setIsRenaming(true);
    setTimeout(() => renameInputRef.current?.focus(), 0);
  }

  function handleRenameSubmit() {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== session.name) {
      renameSession.mutate(trimmed);
    }
    setIsRenaming(false);
  }

  function handleRenameKeyDown(e: React.KeyboardEvent) {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      setIsRenaming(false);
    }
  }

  const isActive = ['active', 'processing', 'waiting_for_input', 'idle'].includes(session.status);

  return (
    <>
      <div
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && !isRenaming && handleClick()}
        role="button"
        tabIndex={0}
        className={`group card cursor-pointer px-4 py-3 transition-all hover:shadow-sm${isActive ? ' card-active card-active-bar' : ''}`}
        style={isTerminated ? { opacity: 0.6 } : undefined}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {isRenaming ? (
                <input
                  ref={renameInputRef}
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={handleRenameKeyDown}
                  onBlur={handleRenameSubmit}
                  onClick={(e) => e.stopPropagation()}
                  maxLength={100}
                  disabled={renameSession.isPending}
                  className="rename-input w-full text-sm font-medium"
                  aria-label="Session name"
                />
              ) : (
                <>
                  {unread && (
                    <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }} role="img" aria-label="Unread" title="New activity since you last read it" />
                  )}
                  <span className={`truncate text-sm ${unread ? 'font-semibold' : 'font-medium'}`} style={{ color: 'var(--color-text)' }}>
                    {session.name || truncateId(session.id)}
                  </span>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={handleRenameClick}
                      className="rename-icon-btn flex-shrink-0 opacity-0 transition-all group-hover:opacity-100"
                      aria-label="Rename session"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                      </svg>
                    </button>
                  )}
                </>
              )}
              {permissionPending && (
                <span className="badge badge-warn">Permission</span>
              )}
              {/* A decision of the operator it waits on (#918): it is not stuck */}
              {awaitingDecision && !isTerminated && <span className="badge badge-warn">Waiting on you</span>}
              <SessionStatusBadge status={session.status} />
            </div>
            <div className="mt-1 flex items-center gap-3 text-xs" style={{ color: 'var(--color-text-3)' }}>
              <span className="text-mono">{truncateId(session.id)}</span>
              <span className={`badge-model badge-model-${session.model}`}>{MODEL_LABELS[session.model] ?? session.model}</span>
              {/* The list is ordered by it: when it was last active, its creation on hover */}
              <span title={`Created ${new Date(session.createdAt).toLocaleString()}`}>{`active ${formatTimeAgo(lastActive(session))}`}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {session.tokenUsage && (
              <span className="text-mono whitespace-nowrap text-xs" style={{ color: 'var(--color-text-3)' }}>
                {formatTokens(session.tokenUsage.inputTokens)}&darr; {formatTokens(session.tokenUsage.outputTokens)}&uarr;
              </span>
            )}

            {isAdmin && isTerminated && (
              <Button
                variant="icon"
                size="sm"
                onClick={handleRestoreClick}
                disabled={restoreSession.isPending}
                aria-label="Restore session"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
                </svg>
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="icon"
                size="sm"
                onClick={handleDeleteClick}
                disabled={deleteSession.isPending}
                aria-label="Delete session"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
              </Button>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteDialog}
        title="Delete session"
        message="This will terminate and delete the session. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
});
